import React, { useEffect, useState } from 'react';
import {
  Avatar,
  Backdrop,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Grid,
  IconButton,
  Typography,
} from '@mui/material';
import PropTypes from 'prop-types';
import Paper from '@mui/material/Paper';
import { styled } from '@mui/material/styles';
import { ReactComponent as DeleteIcon } from '../../../../assets/common-assets/trash-red.svg';
import { Root, classes } from './utils';
import AgGrid from '../../common-components/common-ag-grid/AgGrid';
// import memberColumnDef from './memberColumnDef';
import { deleteUser, getMembersForGird } from '../../../../services/userApi';
import { globalClasses } from '../../utils/globalUtils';
import AddMember from './AddMember';
import { getGridFilterSortStatus } from '../../../../services/allInvoiceApi';
import { getLocalStorageItem } from '../../../../services/localStorageUtils';
import ROLES from '../../../../constants/roles';
import { lightPalette } from '../../../../theme';
import invitedMembersColumnDef from './invitedMembersColumnDef';
import closeIcon from '../../../../assets/accountSetting-assets/x-circle.svg';
import { invitedSubscriberDetails } from '../../../../services/subscriberDetails';

const Members = React.memo(function Members({ organizationId }) {
  const [open, setOpen] = useState(false);
  const [invitationEmail, setInvitationEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [gridSettingData, setGridSettingData] = useState({});
  const [roleError, setRoleError] = useState(null);
  const [selectedRole, setSelectedRole] = useState(null);
  const loginRole = getLocalStorageItem('role');
  const [showCurrentSubscriberTable, setShowCurrentSubscriberTable] =
    useState(true);
  const [showInvitedSubscriberTable, setShowInvitedSubscriberTable] =
    useState(false);
  const [currentMembers, setCurrentMembers] = useState([]);
  const [memberToDelete, setMemberToDelete] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedExpiration, setSelectedExpiration] = useState({
    title: '2 Days',
    time: '2d',
  });

  const handleOpen = () => {
    setOpen(true);
  };

  const handleExpireTimeValue = (newValue) => {
    setSelectedExpiration(newValue);
  };
  const handleOpenDialog = (member) => {
    setMemberToDelete(member);
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setMemberToDelete(null);
    setIsDialogOpen(false);
  };

  const fetchMembers = async () => {
    try {
      const currentData = await getMembersForGird(
        {},
        '',
        {},
        null,
        organizationId,
      );
      setCurrentMembers(currentData || []);
    } catch (error) {
      console.error('Error fetching members:', error);
    }
  };

  const handleConfirmAction = async () => {
    try {
      if (memberToDelete) {
        const response = await deleteUser(memberToDelete.id); // Call your delete API
        if (response) {
          await fetchMembers(); // Refresh members after deletion
        }
      }
    } catch (error) {
      console.error('Error deleting member:', error);
    } finally {
      handleCloseDialog();
    }
  };

  const handleTableButton = async () => {
    setShowCurrentSubscriberTable(true);
    setShowInvitedSubscriberTable(false);
  };

  const handleChartButton = async () => {
    setShowCurrentSubscriberTable(false);
    setShowInvitedSubscriberTable(true);
  };

  useEffect(() => {
    fetchMembers();
  }, [showCurrentSubscriberTable]);

  const handleClose = () => {
    setInvitationEmail('');
    setEmailError('');
    setRoleError(null);
    setSelectedRole(null);
    setOpen(false);
    setSelectedExpiration({
      title: '2 Days',
      time: '2d',
    });
  };

  const getFilterSortData = async () => {
    try {
      const response = await getGridFilterSortStatus('users');
      setGridSettingData(response);
    } catch (err) {
      return err;
    }
  };

  useEffect(() => {
    getFilterSortData();
  }, []);

  const renderMemberCard = (member) => {
    const {
      first_name: firstName,
      last_name: lastName,
      email,
      is_active: isActive,
      phone_no: phoneNo,
      role,
      country,
      updated_at: updatedAt,
      avatar,
      updated_by: updatedBy,
    } = member;

    const formattedUpdatedAt = updatedAt
      ? new Date(updatedAt).toLocaleString()
      : 'N/A';

    return (
      <Box p={2} display="flex" gap={4}>
        {/* Avatar */}
        <Avatar
          className={classes.avatar}
          src={avatar || undefined}
          alt={`${firstName} ${lastName} Avatar`}
        >
          {!avatar && firstName && lastName ? (
            <Typography
              variant="h5"
              style={{ fontSize: '1.5rem', fontWeight: 'bold' }}
            >
              {`${firstName[0]}${lastName[0]}`.toUpperCase()}
            </Typography>
          ) : null}
        </Avatar>

        {/* Card Details */}
        <Box display="flex" flex={1} gap={2} flexDirection="column">
          <Box display="flex" justifyContent="space-between">
            {/* Member Name and Status */}
            <Box>
              <Box display="flex" alignItems="center" gap={2}>
                <Typography>{`${firstName} ${lastName}`}</Typography>
                <Typography
                  bgcolor={isActive ? '#28a745' : '#dc3545'}
                  color="white"
                  padding="2px 12px"
                  borderRadius="999px"
                  textAlign="center"
                >
                  {isActive ? 'Active' : 'Inactive'}
                </Typography>
              </Box>
              <Typography paddingTop={1}>{email}</Typography>
            </Box>

            {/* Delete Icon */}
            <Box className={classes.deleteIcon}>
              <IconButton
                color="primary"
                aria-label="delete"
                onClick={() => handleOpenDialog(member)}
              >
                <DeleteIcon />
              </IconButton>
            </Box>
          </Box>

          <Divider variant="fullWidth" />

          {/* Member Details Grid */}
          <Grid container width="100%">
            <Grid container item xs={6}>
              <Grid item xs={6}>
                <Typography>Role</Typography>
                <Typography>Country</Typography>
                <Typography>Updated at</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography>{role?.role || 'N/A'}</Typography>
                <Typography>{country?.country_name || 'N/A'}</Typography>
                <Typography>{formattedUpdatedAt}</Typography>
              </Grid>
            </Grid>
            <Grid container item xs={6}>
              <Grid item xs={6}>
                <Typography>Phone No.</Typography>
                <Typography>Updated By</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography>{phoneNo || 'N/A'}</Typography>
                <Typography>{updatedBy || 'N/A'}</Typography>
              </Grid>
            </Grid>
          </Grid>
        </Box>
      </Box>
    );
  };

  const Item = styled(Paper)(({ theme }) => ({
    backgroundColor: '#f5f1fd',
    ...theme.typography.body2,
    padding: theme.spacing(1),
    color: theme.palette.text.secondary,
    ...theme.applyStyles('dark', {
      backgroundColor: '#1A2027',
    }),
  }));

  return (
    <Root className={classes.root}>
      {loginRole === ROLES.ADMIN || loginRole === ROLES.MANAGER ? (
        <Box className={classes.addMoreButtonBox}>
          <Box className={classes.Table_Chart_Button_Box}>
            <Box
              alignSelf="center"
              className={classes.Table_Chart_Button_Inner_wrapped_Box}
              onClick={handleOpen}
            >
              <Button className={classes.ADD_User_Button} variant="text">
                + Add More User
              </Button>
            </Box>
            <Box className={classes.Table_Chart_Button_Inner_wrapped_Box}>
              <Button
                className={classes.Table_Chart_Button}
                onClick={handleTableButton}
                variant={showCurrentSubscriberTable ? 'contained' : 'text'}
                sx={{
                  background: showCurrentSubscriberTable
                    ? lightPalette.japanBlush.main
                    : null,
                  color: showCurrentSubscriberTable
                    ? lightPalette.darkPurple.main
                    : lightPalette.lunarBase.main,
                  ':hover': {
                    background: showCurrentSubscriberTable
                      ? lightPalette.japanBlush.main
                      : null,
                    color: showCurrentSubscriberTable
                      ? lightPalette.darkPurple.main
                      : lightPalette.lunarBase.main,
                  },
                }}
              >
                Current
              </Button>
              <Button
                className={classes.Table_Chart_Button}
                onClick={handleChartButton}
                variant={showInvitedSubscriberTable ? 'contained' : 'text'}
                sx={{
                  background: showInvitedSubscriberTable
                    ? lightPalette.japanBlush.main
                    : null,
                  color: showInvitedSubscriberTable
                    ? lightPalette.darkPurple.main
                    : lightPalette.lunarBase.main,
                  ':hover': {
                    background: showInvitedSubscriberTable
                      ? lightPalette.japanBlush.main
                      : null,
                    color: showInvitedSubscriberTable
                      ? lightPalette.darkPurple.main
                      : lightPalette.lunarBase.main,
                  },
                }}
              >
                Invited
              </Button>
            </Box>
          </Box>
          <Box>
            <Backdrop className={globalClasses.backdrop} open={open}>
              <AddMember
                handleClose={handleClose}
                invitationEmail={invitationEmail}
                setInvitationEmail={setInvitationEmail}
                emailError={emailError}
                setEmailError={setEmailError}
                roleError={roleError}
                setRoleError={setRoleError}
                selectedRole={selectedRole}
                setSelectedRole={setSelectedRole}
                selectedExpiration={selectedExpiration}
                handleExpireTimeValue={handleExpireTimeValue}
              />
            </Backdrop>
          </Box>
        </Box>
      ) : null}
      {/* {showCurrentSubscriberTable && (
        <AgGrid
          TitleHeader="Members"
          className={loginRole !== ROLES.MEMBER ? classes.membersAgGridStyle : classes.moreHeight}
          isFilter
          columnData={memberColumnDef}
          getRowData={getMembersForGird}
          gridSettingData={gridSettingData}
          gridKey="users"
          functionData={organizationId}
          setOpen={setOpen}
        />
      )} */}
      {showInvitedSubscriberTable && (
        <AgGrid
          TitleHeader="Invited Members"
          className={
            loginRole !== ROLES.MEMBER
              ? classes.membersAgGridStyle
              : classes.moreHeight
          }
          isFilter
          columnData={invitedMembersColumnDef}
          getRowData={invitedSubscriberDetails}
          gridSettingData={gridSettingData}
          gridKey="invited-users"
          setOpen={setOpen}
          setGridSettingData={setGridSettingData}
        />
      )}
      {showCurrentSubscriberTable && (
        <Box marginTop="2.037vh">
          <Box sx={{ width: '100%' }}>
            <Grid
              className={classes.memberCards}
              container
              rowSpacing={1}
              columnSpacing={{ xs: 1, sm: 2, md: 3 }}
            >
              {currentMembers.map((elem, index) => (
                <Grid
                  borderRadius={1}
                  item
                  xs={12}
                  sm={12}
                  lg={6}
                  key={`${index + 1}`}
                  height="fit-content"
                  sx={{ paddingBottom: '16px !important' }}
                >
                  <Item>{renderMemberCard(elem)}</Item>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Box>
      )}

      <Backdrop className={classes.newFlowBackdrop} open={isDialogOpen}>
        <Card className={classes.cardComponent}>
          <CardHeader
            className={classes.DeleteMemberCardHeader}
            title={
              <Grid container alignItems="center">
                <Grid item xs={10}>
                  <Typography variant="h4">Delete Member</Typography>
                </Grid>
                <Grid item xs={2} align="right">
                  <Avatar
                    onClick={handleCloseDialog}
                    src={closeIcon}
                    className={classes.closeAction}
                  />
                </Grid>
              </Grid>
            }
          />

          <CardContent className={classes.DeleteMemberCardContent}>
            <Typography>
              Are you sure you want to delete{' '}
              {memberToDelete?.role?.role || 'Member'}:
              {memberToDelete?.first_name} {memberToDelete?.last_name}?
            </Typography>
            <Box className={classes.actionButtonBox}>
              <Button
                className={classes.cancelButton}
                onClick={handleCloseDialog}
              >
                <Typography variant="body1">No</Typography>
              </Button>
              <Button
                variant="contained"
                className={classes.saveButton}
                onClick={() => handleConfirmAction()}
              >
                <Typography variant="body1">Yes</Typography>
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Backdrop>
    </Root>
  );
});

export default Members;

Members.propTypes = {
  organizationId: PropTypes.string.isRequired,
};
